<script setup>
/**
 * "What comes back?" — the one control that collapses three step kinds and an answer kind
 * into a single question.
 *
 * `TaskStepTypePicker.vue`'s markup again — rows with one sentence each, in place, never a
 * `<select>` and never a modal — but as a RADIO GROUP rather than a menu, because this is a
 * setting with a current value rather than an action that inserts something. It is one
 * control on the step screen and it reads as one: `role="radiogroup"` with a label, eight
 * `role="radio"` rows, and the dot where the owner left it.
 *
 * ── THE SEAM AT ROW 7, DRAWN RATHER THAN HIDDEN ───────────────────────────────────────
 * Rows 1-6 say what the answer has to LOOK like; rows 7-8 say where the choices COME from.
 * They are not the same kind of thing, and an owner reading top to bottom meets that change
 * with nothing to mark it. `helpers/task-answer-kinds.js` carries the grouping as data and
 * this prints one rule between the halves. If the eight ever read as eight peers, the
 * grouping is already here to split them into two controls without touching a caller.
 *
 * ── AND THE ROW THAT IS DELIBERATELY NOT LIKE THE OTHERS ──────────────────────────────
 * Row 8 writes a `choose_live`, which cannot carry a customer-facing question at all
 * (`types.ts:131`, `schema.ts:40-43`, under `.strict()`). The step screen is what acts on
 * that; this only has to report the row honestly, which is why the sentence under it says
 * the choices arrive while the customer waits.
 */
import { computed } from 'vue';

import { useWasloI18n } from '../../i18n.js';
import {
  ANSWER_GROUPS,
  ANSWER_GROUP_RULE_KEY,
  ANSWER_ROWS,
} from '../../helpers/task-answer-kinds.js';

const props = defineProps({
  /** A row id from `ANSWER_ROWS`, or `''` when the stored step matches none of them. */
  modelValue: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const ICONS = {
  text: 'i-lucide-pencil-line',
  number: 'i-lucide-hash',
  date: 'i-lucide-calendar-days',
  time: 'i-lucide-clock',
  email: 'i-lucide-at-sign',
  phone: 'i-lucide-phone',
  written: 'i-lucide-list',
  live: 'i-lucide-list-restart',
};

const rowsIn = group =>
  ANSWER_ROWS.filter(row => row.group === group).map(row => ({
    id: row.id,
    label: t(row.labelKey),
    hint: t(row.hintKey),
    icon: ICONS[row.id] ?? 'i-lucide-circle',
    chosen: row.id === props.modelValue,
  }));

/**
 * Two blocks with the rule between them, each two columns wide.
 *
 * ONE COLUMN OF EIGHT IS TOO TALL, and that is not taste: at 1440×900 it pushed the system
 * picker, the wiring card and the phone preview — the three controls the rest of this step
 * is about — entirely below the fold, so the screen whose whole claim is "five controls"
 * showed two. Two columns puts the eight in four rows and the rest back on screen.
 */
const shapeRows = computed(() => rowsIn(ANSWER_GROUPS.SHAPE));
const sourceRows = computed(() => rowsIn(ANSWER_GROUPS.SOURCE));
</script>

<template>
  <div class="flex flex-col gap-2" data-waslo-answer-picker>
    <p
      id="waslo-answer-picker-label"
      class="mb-0 text-sm font-medium text-n-slate-12"
    >
      {{ t('WASLO.TASKS.COMPOSE.ANSWER.HEADING') }}
    </p>
    <div
      role="radiogroup"
      aria-labelledby="waslo-answer-picker-label"
      class="flex flex-col gap-1"
    >
      <div class="grid gap-1 md:grid-cols-2">
        <button
          v-for="row in shapeRows"
          :key="row.id"
          type="button"
          role="radio"
          :aria-checked="row.chosen ? 'true' : 'false'"
          class="flex items-start w-full gap-3 px-3 py-2 text-start rounded-lg outline outline-1"
          :class="
            row.chosen
              ? 'outline-n-blue-8 bg-n-blue-2'
              : 'outline-transparent hover:bg-n-alpha-2'
          "
          :data-waslo-answer-row="row.id"
          :data-waslo-answer-chosen="row.chosen ? 'yes' : 'no'"
          @click="emit('update:modelValue', row.id)"
        >
          <span
            class="mt-1 shrink-0 size-4"
            :class="[
              row.icon,
              row.chosen ? 'text-n-blue-11' : 'text-n-slate-11',
            ]"
            aria-hidden="true"
          />
          <span class="flex flex-col min-w-0">
            <span
              class="text-sm font-medium"
              :class="row.chosen ? 'text-n-blue-11' : 'text-n-slate-12'"
            >
              {{ row.label }}
            </span>
            <span class="text-sm text-n-slate-11">{{ row.hint }}</span>
          </span>
        </button>
      </div>

      <p class="mb-0 mt-2 text-sm text-n-slate-11" data-waslo-answer-rule>
        {{ t(ANSWER_GROUP_RULE_KEY) }}
      </p>

      <div class="grid gap-1 md:grid-cols-2">
        <button
          v-for="row in sourceRows"
          :key="row.id"
          type="button"
          role="radio"
          :aria-checked="row.chosen ? 'true' : 'false'"
          class="flex items-start w-full gap-3 px-3 py-2 text-start rounded-lg outline outline-1"
          :class="
            row.chosen
              ? 'outline-n-blue-8 bg-n-blue-2'
              : 'outline-transparent hover:bg-n-alpha-2'
          "
          :data-waslo-answer-row="row.id"
          :data-waslo-answer-chosen="row.chosen ? 'yes' : 'no'"
          @click="emit('update:modelValue', row.id)"
        >
          <span
            class="mt-1 shrink-0 size-4"
            :class="[
              row.icon,
              row.chosen ? 'text-n-blue-11' : 'text-n-slate-11',
            ]"
            aria-hidden="true"
          />
          <span class="flex flex-col min-w-0">
            <span
              class="text-sm font-medium"
              :class="row.chosen ? 'text-n-blue-11' : 'text-n-slate-12'"
            >
              {{ row.label }}
            </span>
            <span class="text-sm text-n-slate-11">{{ row.hint }}</span>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
